import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, map, of, throwError } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ASSET_TYPES, AssetType } from "../../../enums/assetTypes.enum";
import { REFERSH } from "../../../interceptors/cache/cache.interceptor";
import {
  TenantCompact,
  CustomerCompact,
  BuildingCompact,
  GateSystemCompact,
} from "../../../models/api/asset/assetCompact";
import { AssetHierarchyDTO } from "../../../models/api/asset/assetHierarchyDTO";
import { Parents, BotevoAssetCompact, BotevoAsset, BotevoAssetDTO } from "../../../models/api/asset/botevoAsset";
import { Building, BuildingDTO } from "../../../models/api/asset/building";
import { Customer, CustomerDTO } from "../../../models/api/asset/customer";
import { GateSystem, GateSystemDTO } from "../../../models/api/asset/gateSystem";
import { LocationDTO, Location } from "../../../models/api/asset/location";
import { TenantDTO, Tenant } from "../../../models/api/tenant";
import { ResponseSuccess } from "../../../models/shared/responseSuccess";
import { getAssetParentType, isBotevoAssetType } from "../../../shared/utils/assetType.utils";
import { AssetHierarchyService } from "../asset-hierarchy/asset-hierarchy.service";
import { PermissionService } from "../permission/permission.service";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";

@Injectable({
  providedIn: "root",
})
export class AssetService {
  private http = inject(HttpClient);
  private assetHierarchyService = inject(AssetHierarchyService);
  private permissions = inject(PermissionService);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Finds the parents of a given asset in the hierarchy and returns them as an object
   * with the parent types as keys and the parent ids as values.
   *
   * @param hierarchy the asset hierarchy
   * @param targetId the id of the asset to find the parents for
   * @param targetType the type of the asset to find the parents for
   * @param parents the current parents object
   * @returns the parents object
   */
  private findAssetParents(
    hierarchy: AssetHierarchyDTO[],
    targetId: string,
    targetType: string | null,
    parents: Parents = {}
  ): Parents | null {
    for (const asset of hierarchy) {
      if (asset.id === targetId && asset.assetType === targetType) {
        return parents;
      }
      if (asset.children && asset.children.length > 0) {
        const result = this.findAssetParents(asset.children, targetId, targetType, {
          ...parents,
          [asset.assetType.toLowerCase()]: { id: asset.id },
        });
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  private findGateSystemIds(hierarchy: AssetHierarchyDTO[]): string[] {
    const gateSystems: string[] = [];
    for (const asset of hierarchy) {
      if (asset.assetType === ASSET_TYPES.GATE_SYSTEM) {
        gateSystems.push(asset.id);
      }
      if (asset.children && asset.children.length > 0) {
        gateSystems.push(...this.findGateSystemIds(asset.children).filter((id) => id));
      }
    }
    return gateSystems;
  }

  public getAllGateSystems(): Observable<string[]> {
    return this.assetHierarchyService.getAssetHierarchy(ASSET_TYPES.GATE_SYSTEM, 4).pipe(
      map((res) => {
        if (res) {
          return this.findGateSystemIds(res);
        } else {
          return [];
        }
      })
    );
  }
  public getAssets(
    type: ASSET_TYPES.TENANT,
    parentId?: string,
    parentType?: string,
    refresh?: boolean
  ): Observable<TenantCompact[]>;
  public getAssets(
    type: ASSET_TYPES.LOCATION,
    parentId?: string,
    parentType?: string,
    refresh?: boolean
  ): Observable<CustomerCompact[]>;
  public getAssets(
    type: ASSET_TYPES.BUILDING,
    parentId?: string,
    parentType?: string,
    refresh?: boolean
  ): Observable<BuildingCompact[]>;
  public getAssets(
    type: ASSET_TYPES.GATE_SYSTEM,
    parentId?: string,
    parentType?: string,
    refresh?: boolean
  ): Observable<GateSystemCompact[]>;
  public getAssets(
    type: string | null,
    parentId?: string,
    parentType?: string,
    refresh?: boolean
  ): Observable<BotevoAssetCompact[]>;
  public getAssets(
    type: ASSET_TYPES | null | string,
    parentId?: string,
    parentType?: string,
    refresh = false
  ): Observable<BotevoAssetCompact[]> {
    return this.http
      .get<BotevoAssetCompact[]>(environment.baseUrl + `/asset`, {
        params: {
          assetType: type ?? "",
          parentId: parentId ?? "",
          parentType: parentType ?? "",
        },
        context: new HttpContext().set(REFERSH, refresh),
      })
      .pipe(
        this.apiErrorHandler.handle(type?.toLowerCase(), []),
        map((res) => {
          return this.orderByLabel(res) ?? [];
        })
      );
  }

  /**
   * Orders the given array of assets alphabetically by their label.
   * If either asset is missing a label, the order is not changed.
   * @param assets The array of assets to order.
   * @returns The ordered array of assets.
   */
  private orderByLabel(assets: BotevoAssetCompact[]) {
    return assets.sort((a, b) => {
      if (a.label && b.label) {
        return a.label.localeCompare(b.label);
      } else {
        return 0;
      }
    });
  }

  /**
   * Creates a new asset.
   * @param assetType The type of the asset to create.
   * @param asset The asset to create.
   * @param parentId The id of the parent asset, if applicable.
   * @returns An observable that emits the id of the created asset, or null if the creation failed.
   */
  public createAsset(
    assetType: AssetType,
    asset: BotevoAsset,
    parentId: string | undefined
  ): Observable<string | null> {
    if (parentId && (asset instanceof GateSystem || asset instanceof Building)) {
      const parentType = getAssetParentType(assetType);

      const parents = this.findAssetParents(this.assetHierarchyService.assetHierarchy, parentId, parentType);

      if (parents) {
        asset.parents = parents;
      }
    }

    return this.http.post<BotevoAsset>(environment.baseUrl + "/asset?assetType=" + assetType, asset.values).pipe(
      this.apiErrorHandler.handle(assetType.toLowerCase()),
      map((res: BotevoAsset) => {
        this.assetHierarchyService.refreshAssetHierarchy();
        return res.id ?? null;
      })
    );
  }

  /**
   * Edits an existing asset of the specified type.
   * Sends a PUT request to update the asset's information on the server.
   * If the update is successful, emits an observable with a success response.
   * Otherwise, emits an observable with a failure response.
   *
   * @param assetType The type of the asset to edit.
   * @param asset The asset object containing updated values.
   * @returns An observable that emits a success response if the update was successful,
   * otherwise emits a failure response.
   */

  public editAsset(assetType: string, asset: BotevoAsset): Observable<string | null> {
    if (
      asset instanceof GateSystem &&
      this.permissions.missesPermission(this.permissions.allPermissions.ASSET_MASTER_DATA_CREATE)
    ) {
      delete asset.gateHistoryCycle;
      delete asset.activeDate;
    }
    return this.http
      .put<BotevoAsset>(environment.baseUrl + "/asset/" + asset.id + "?assetType=" + assetType, asset.values)
      .pipe(
        this.apiErrorHandler.handle(assetType.toLowerCase()),

        map((res) => {
          this.assetHierarchyService.refreshAssetHierarchy();
          return res.id ?? null;
        })
      );
  }

  /**
   * Deletes an asset of the given type and id.
   * @param assetType The type of the asset to delete.
   * @param assetId The id of the asset to delete.
   * @returns An observable that emits a success response if the deletion was successful,
   * otherwise emits a failure response.
   */

  public deleteAsset(assetType: string, assetId: string): Observable<ResponseSuccess> {
    return this.http
      .delete(environment.baseUrl + "/asset/" + assetId + "?assetType=" + assetType, {
        observe: "response",
      })
      .pipe(
        this.apiErrorHandler.handle(assetType.toLowerCase()),
        map((res) => {
          if (res.status === 200) {
            this.assetHierarchyService.refreshAssetHierarchy();
            return { success: true };
          } else {
            return { success: false };
          }
        })
      );
  }

  /**
   * Returns an observable that emits an empty asset of the specified type.
   * If a parent asset is provided, it is used to populate the appropriate fields.
   * @param assetType The type of the asset to create.
   * @param parent The parent asset, if applicable.
   * @returns An observable that emits an empty asset of the specified type.
   */
  public getEmptyAsset(assetType: ASSET_TYPES.CUSTOMER): Observable<Customer>;
  public getEmptyAsset(assetType: ASSET_TYPES.LOCATION, parent?: BotevoAsset): Observable<Location>;
  public getEmptyAsset(assetType: ASSET_TYPES.BUILDING, parent?: BotevoAsset): Observable<Building>;
  public getEmptyAsset(assetType: ASSET_TYPES.GATE_SYSTEM, parent?: BotevoAsset): Observable<GateSystem>;
  public getEmptyAsset(assetType: string, parent?: BotevoAsset): Observable<BotevoAsset | null>;
  public getEmptyAsset(assetType: ASSET_TYPES | string, parent?: BotevoAsset): Observable<BotevoAsset | null> {
    switch (assetType) {
      case ASSET_TYPES.CUSTOMER:
        return of(new Customer({ label: "", address: { country: "DE" } }));
      case ASSET_TYPES.LOCATION:
        return of(
          new Location({
            label: "",
            customer: { id: parent?.id },
            address: { country: "DE" },
          })
        );
      case ASSET_TYPES.BUILDING: {
        const parentLocation = parent;
        if (parentLocation instanceof Location) {
          const buildingObject: BuildingDTO = {
            label: "",
            location: { id: parent?.id },
            address: parentLocation?.address,
          };
          if (parentLocation?.floorMapUrl && buildingObject.location) {
            buildingObject.location.locationFloorMapUrl = parentLocation.floorMapUrl;
          }
          return of(new Building(buildingObject));
        } else {
          return throwError(() => "Parent is not a location");
        }
      }
      case ASSET_TYPES.GATE_SYSTEM: {
        const parentBuilding = parent as Building;
        const gateSystemObject: GateSystemDTO = {
          label: "",
          building: { id: parent?.id },
          isSluiceGate: false,
          quickupGate: "none",
          loadingBridge: "none",
          emailEnergyLoss: false,
          hasButler: false,
          core: true,
        };
        if (parentBuilding.floorMapUrl && gateSystemObject.building) {
          gateSystemObject.building.buildingFloorMapUrl = parentBuilding.floorMapUrl;
        }
        return of(new GateSystem(gateSystemObject));
      }
      default:
        return throwError(() => "Invalid asset type");
    }
  }

  /**
   * Fetches the details of an asset by its ID and type.
   * @param assetId The ID of the asset to retrieve.
   * @param assetType The type of the asset to retrieve.
   * @param refresh Optional flag to refresh the asset details.
   * @returns An observable that emits the asset details as a BotevoAsset.
   * @throws An error if the response does not contain a body.
   */
  public getAssetDetails(assetId: string, assetType: string, refresh = false): Observable<BotevoAsset | null> {
    if (isBotevoAssetType(assetType)) {
      return this.http
        .get<TenantDTO | CustomerDTO | LocationDTO | BuildingDTO | GateSystemDTO>(
          environment.baseUrl + `/asset/${assetId}`,
          {
            params: {
              assetType: assetType ? assetType : "",
            },
            context: new HttpContext().set(REFERSH, refresh),
          }
        )
        .pipe(
          this.apiErrorHandler.handle(assetType.toLowerCase(), null),
          map((res) => {
            if (res) {
              return this.convertToAsset(assetType, res);
            } else {
              return null;
            }
          })
        );
    } else {
      return throwError(() => "Invalid asset type");
    }
  }

  /**
   * Converts the server response body to a BotevoAsset object.
   * @param assetType The type of the asset to convert.
   * @param serverResBody The server response body containing the asset details.
   * @returns A BotevoAsset object representing the asset.
   * @throws An error if the asset type is not supported.
   */
  private convertToAsset(assetType: string, serverResBody: BotevoAssetDTO): BotevoAsset {
    switch (assetType) {
      case ASSET_TYPES.TENANT: {
        const tenantDTO = serverResBody as TenantDTO;
        return new Tenant(tenantDTO);
      }
      case ASSET_TYPES.CUSTOMER: {
        const customerDTO = serverResBody as CustomerDTO;
        return new Customer(customerDTO);
      }
      case ASSET_TYPES.LOCATION: {
        const locationDTO = serverResBody as LocationDTO;
        return new Location(locationDTO);
      }
      case ASSET_TYPES.BUILDING: {
        const buildingDTO = serverResBody as BuildingDTO;
        return new Building(buildingDTO);
      }
      case ASSET_TYPES.GATE_SYSTEM: {
        const gateSystemDTO = serverResBody as GateSystemDTO;
        return new GateSystem(gateSystemDTO);
      }
      default:
        // Provide a default case to handle unexpected asset types
        throw new Error(`Unsupported asset type: ${assetType}`);
    }
  }
}
